<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watchEffect } from 'vue'
import Icon from '@/components/Icon.vue'
import PulseDot from './PulseDot.vue'
import type { DeployStepEntry } from '@/store/deployProgress'

/**
 * Visual progress panel for the dock-onboarding "Deploy" step.
 *
 * Drivers (in priority order):
 *   1. `failed=true` — render the failure state with `errorReason` and
 *      highlight the failed step from `rawSteps`.
 *   2. Parent-driven `currentStage` — FLY3D-612's WebSocket path. The
 *      live backend `currentMessage` displays beneath the active stage,
 *      and `rawSteps` powers the "View backend steps" disclosure.
 *   3. Auto-timeline (no props) — preserved for standalone dev mounts;
 *      the panel runs through the five stages on a fixed timeline.
 *
 * The five abstract stages collapse the backend's 12 concrete steps:
 *  1. Verifying device entitlement       — deploy_started, ecr_authenticated
 *  2. Packaging IDI-API for <dock>       — repo_cloned, versions_resolved, config_updated
 *  3. Uploading & installing on device   — services_stopped, images_pulled, services_started
 *  4. Running post-install health checks — redis_healthy, redis_seeded
 *  5. Deploy complete                    — deploy_complete
 */

interface DeployStage {
  label: string
}

const STAGES: DeployStage[] = [
  { label: 'Verifying device entitlement' },
  { label: 'Packaging IDI-API for Harbor-01' },
  { label: 'Uploading & installing on device' },
  { label: 'Running post-install health checks' },
  { label: 'Deploy complete' },
]

const STAGE_DELAYS_MS = [800, 2800, 5800, 9800, 13800]

const props = withDefaults(
  defineProps<{
    /**
     * Index of the next-to-complete stage. When omitted, the panel
     * auto-runs the demo timeline — useful inside the dev mount and
     * anywhere the real driver hasn't been wired.
     */
    currentStage?: number
    /** Display copy for the dock the deploy is targeted at. */
    targetName?: string
    /** Live backend message shown beneath the active stage. */
    currentMessage?: string | null
    /** Raw 12-step trail from IFB-65 — drives the details disclosure. */
    rawSteps?: DeployStepEntry[]
    /** Render failure state instead of progress. */
    failed?: boolean
    /** Human-readable failure reason (only used when `failed`). */
    errorReason?: string | null
    successFootnote?: string
    /**
     * Wall-clock ms when the deploy started. When supplied, elapsed is
     * derived from `Date.now() - startedAt` so the timer survives the
     * panel being unmounted / remounted (e.g. modal closed and reopened).
     */
    startedAt?: number | null
  }>(),
  {
    targetName: 'Harbor-01',
    currentMessage: null,
    rawSteps: () => [],
    failed: false,
    errorReason: null,
    successFootnote: 'Deployment complete.',
    startedAt: null,
  },
)

const emit = defineEmits<{
  (e: 'complete'): void
}>()

const internalDone = ref(0)
const internalElapsed = ref(0)
const now = ref(Date.now())
// When `startedAt` is provided, derive elapsed from wall-clock so the
// timer reflects the actual deploy duration even if the panel unmounts
// and remounts mid-run. Otherwise fall back to the local tick counter
// (auto-timeline / standalone mount paths).
const elapsed = computed(() => {
  if (props.startedAt) {
    return Math.max(0, Math.floor((now.value - props.startedAt) / 1000))
  }
  return internalElapsed.value
})
const detailsOpen = ref(false)

const doneCount = computed(() =>
  props.currentStage !== undefined
    ? Math.min(props.currentStage, STAGES.length)
    : internalDone.value,
)
const complete = computed(() => doneCount.value >= STAGES.length)

const mm = computed(() => String(Math.floor(elapsed.value / 60)).padStart(2, '0'))
const ss = computed(() => String(elapsed.value % 60).padStart(2, '0'))

const failedStep = computed(() => {
  if (!props.failed || !props.rawSteps?.length) return null
  // Prefer an explicit `deploy_failed` entry, else the most recent step.
  const explicit = props.rawSteps.find((s) => s.step === 'deploy_failed')
  return explicit ?? props.rawSteps[props.rawSteps.length - 1] ?? null
})

const formatTimestamp = (ts?: string) => {
  if (!ts) return ''
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ts
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

let stageTimers: ReturnType<typeof setTimeout>[] = []
let elapsedInterval: ReturnType<typeof setInterval> | null = null
let completeEmitted = false
let completeTimer: ReturnType<typeof setTimeout> | null = null

onMounted(() => {
  // Auto-advance only when the parent isn't driving via `currentStage`.
  if (props.currentStage === undefined) {
    stageTimers = STAGE_DELAYS_MS.map((ms, i) =>
      setTimeout(() => {
        internalDone.value = i + 1
      }, ms),
    )
  }

  elapsedInterval = setInterval(() => {
    if (complete.value || props.failed) return
    if (props.startedAt) {
      now.value = Date.now()
    } else {
      internalElapsed.value++
    }
  }, 1000)
})

onUnmounted(() => {
  stageTimers.forEach((t) => clearTimeout(t))
  if (elapsedInterval) clearInterval(elapsedInterval)
  if (completeTimer) clearTimeout(completeTimer)
})

// Emit `complete` once, on a short delay so the success state has a
// moment to render before the parent advances to Configure. We never
// emit when `failed` — the parent handles the failure path explicitly.
const flushComplete = () => {
  if (completeEmitted || !complete.value || props.failed) return
  completeEmitted = true
  completeTimer = setTimeout(() => emit('complete'), 1800)
}
// `complete` is computed from props OR internal state — watching it
// covers both drivers (auto-timeline and parent-driven).
watchEffect(flushComplete)
</script>

<template>
  <div class="deploy-panel" :data-complete="complete || null" :data-failed="failed || null">
    <!-- Pulsing ring with center icon -->
    <div class="deploy-panel__ring">
      <template v-if="!complete && !failed">
        <span class="deploy-panel__ring-pulse" />
        <span class="deploy-panel__ring-pulse deploy-panel__ring-pulse--delayed" />
      </template>
      <div
        class="deploy-panel__ring-core"
        :data-complete="complete || null"
        :data-failed="failed || null"
      >
        <Icon
          :name="failed ? 'x_circle' : complete ? 'check' : 'bolt'"
          :size="24"
          :color="failed ? 'var(--danger-light)' : 'var(--idi-green)'"
          class="deploy-panel__ring-icon"
        />
      </div>
    </div>

    <div
      class="deploy-panel__eyebrow"
      :data-complete="complete || null"
      :data-failed="failed || null"
    >
      {{
        failed ? 'Deployment failed' : complete ? 'Deployment successful' : 'Deploying to device'
      }}
    </div>
    <h2 class="deploy-panel__heading">
      {{
        failed
          ? `Could not install IDI-API on ${targetName}`
          : complete
            ? `IDI-API installed on ${targetName}`
            : `Installing IDI-API on ${targetName}`
      }}
    </h2>
    <div v-if="!complete && !failed" class="deploy-panel__elapsed">
      Elapsed
      <span class="deploy-panel__elapsed-num">{{ mm }}:{{ ss }}</span>
    </div>
    <div v-else class="deploy-panel__elapsed-spacer" />

    <p v-if="failed && errorReason" class="deploy-panel__error-reason">
      {{ errorReason }}
    </p>
    <p v-else-if="!complete && currentMessage" class="deploy-panel__current-message">
      {{ currentMessage }}
    </p>

    <ul class="deploy-panel__stages">
      <li
        v-for="(s, i) in STAGES"
        :key="s.label"
        class="deploy-panel__stage"
        :data-state="
          failed && i === doneCount
            ? 'failed'
            : i < doneCount
              ? 'done'
              : i === doneCount && !complete
                ? 'active'
                : 'pending'
        "
        :data-last="i === STAGES.length - 1 || null"
      >
        <div class="deploy-panel__stage-bullet">
          <template v-if="failed && i === doneCount">
            <div class="deploy-panel__cross">
              <Icon name="x" :size="11" color="#fff" />
            </div>
          </template>
          <template v-else-if="i < doneCount">
            <div class="deploy-panel__check">
              <Icon name="check" :size="11" color="#fff" />
            </div>
          </template>
          <PulseDot
            v-else-if="i === doneCount && !complete && !failed"
            color="var(--idi-green)"
            :size="10"
          />
          <div v-else class="deploy-panel__stage-empty" />
        </div>
        <span class="deploy-panel__stage-label">{{ s.label }}</span>
        <span class="deploy-panel__stage-meta">
          {{
            failed && i === doneCount
              ? '✕'
              : i < doneCount
                ? '✓'
                : i === doneCount && !complete
                  ? '…'
                  : '—'
          }}
        </span>
      </li>
    </ul>

    <details
      v-if="rawSteps.length > 0"
      class="deploy-panel__details"
      :open="failed || detailsOpen"
      @toggle="(e: Event) => (detailsOpen = (e.target as HTMLDetailsElement).open)"
    >
      <summary class="deploy-panel__details-summary">
        <span>View backend steps</span>
        <span class="deploy-panel__details-count">{{ rawSteps.length }}</span>
      </summary>
      <ol class="deploy-panel__details-list">
        <li
          v-for="(s, i) in rawSteps"
          :key="`${s.step}-${i}`"
          class="deploy-panel__details-item"
          :data-failed-step="failed && failedStep && s.step === failedStep.step ? 'true' : null"
        >
          <span class="deploy-panel__details-step">{{ s.step }}</span>
          <span class="deploy-panel__details-msg">{{ s.message }}</span>
          <span class="deploy-panel__details-time">{{ formatTimestamp(s.timestamp) }}</span>
        </li>
      </ol>
    </details>

    <p class="deploy-panel__footnote">
      {{
        failed
          ? 'Step 2 (the dock SN you typed) is the most likely cause. Check the SN and retry.'
          : complete
            ? successFootnote
            : "You can safely navigate away — we'll notify you when it completes."
      }}
    </p>
  </div>
</template>

<style scoped>
.deploy-panel {
  text-align: center;
  font-family: var(--primary-font);
  color: var(--txt-primary);
}

.deploy-panel__ring {
  position: relative;
  width: 140px;
  height: 140px;
  margin: 20px auto 28px;
}
.deploy-panel__ring-pulse {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid var(--idi-green);
  animation: idi-pulse-ring 2s ease-out infinite;
}
.deploy-panel__ring-pulse--delayed {
  inset: 20px;
  animation-delay: 0.6s;
}
.deploy-panel__ring-core {
  position: absolute;
  inset: 40px;
  border-radius: 50%;
  background: var(--idi-shade2-100);
  border: 1.5px solid var(--idi-shade3-100);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.4s ease;
}
.deploy-panel__ring-core[data-complete] {
  background: var(--idi-green-a25);
  border-color: var(--idi-green);
}
.deploy-panel__ring-core[data-failed] {
  background: var(--danger-alpha);
  border-color: var(--danger-light);
}
.deploy-panel__ring-icon {
  animation: idi-pulse-soft 2s ease-in-out infinite;
}
.deploy-panel[data-complete] .deploy-panel__ring-icon {
  animation: none;
}

.deploy-panel__eyebrow {
  font-size: 0.6875rem;
  color: var(--txt-secondary-1);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 6px;
  transition: color 0.3s;
}
.deploy-panel__eyebrow[data-complete] {
  color: var(--idi-green);
}
.deploy-panel__eyebrow[data-failed] {
  color: var(--danger-light);
}

.deploy-panel__current-message {
  margin: -12px auto 18px;
  max-width: 480px;
  font-size: 0.8125rem;
  color: var(--txt-secondary-1);
  text-align: center;
  line-height: 1.5;
}
.deploy-panel__error-reason {
  margin: -12px auto 18px;
  max-width: 480px;
  font-size: 0.8125rem;
  color: var(--danger-light);
  text-align: center;
  line-height: 1.5;
}

.deploy-panel__heading {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 8px;
}
.deploy-panel__elapsed,
.deploy-panel__elapsed-spacer {
  margin-bottom: 28px;
}
.deploy-panel__elapsed {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 1rem;
  color: var(--txt-secondary-1);
  font-variant-numeric: tabular-nums;
}
.deploy-panel__elapsed-num {
  color: var(--txt-primary);
  font-weight: 700;
}

.deploy-panel__stages {
  list-style: none;
  margin: 0 auto;
  padding: 0;
  max-width: 520px;
  text-align: left;
  background: var(--idi-shade2-100);
  border: 1px solid var(--idi-shade3-50);
  border-radius: var(--rad-xl);
  overflow: hidden;
}
.deploy-panel__stage {
  display: grid;
  grid-template-columns: 24px 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid var(--idi-shade3-25);
  transition: opacity 0.3s;
}
.deploy-panel__stage[data-last] {
  border-bottom: none;
}
.deploy-panel__stage[data-state='pending'] {
  opacity: 0.4;
}
.deploy-panel__stage-bullet {
  display: flex;
  align-items: center;
  justify-content: center;
}
.deploy-panel__check {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--idi-green-s1);
  display: flex;
  align-items: center;
  justify-content: center;
}
.deploy-panel__cross {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--danger-light);
  display: flex;
  align-items: center;
  justify-content: center;
}
.deploy-panel__stage[data-state='failed'] .deploy-panel__stage-label {
  color: var(--danger-light);
  font-weight: 600;
}
.deploy-panel__stage-empty {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid var(--idi-shade3-100);
}
.deploy-panel__stage-label {
  font-size: 0.8125rem;
  color: var(--txt-secondary-2);
  font-weight: 400;
}
.deploy-panel__stage[data-state='done'] .deploy-panel__stage-label {
  color: var(--txt-secondary-1);
}
.deploy-panel__stage[data-state='active'] .deploy-panel__stage-label {
  color: var(--txt-primary);
  font-weight: 600;
}
.deploy-panel__stage-meta {
  font-size: 0.6875rem;
  color: var(--txt-secondary-2);
  font-family: 'JetBrains Mono', ui-monospace, monospace;
}

.deploy-panel__details {
  margin: 16px auto 0;
  max-width: 520px;
  text-align: left;
  font-size: 0.75rem;
  color: var(--txt-secondary-1);
}
.deploy-panel__details-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 8px 14px;
  background: var(--idi-shade2-100);
  border: 1px solid var(--idi-shade3-50);
  border-radius: var(--rad-m);
  list-style: none;
  font-weight: 600;
  letter-spacing: 0.04em;
}
.deploy-panel__details-summary::-webkit-details-marker {
  display: none;
}
.deploy-panel__details-summary:hover {
  border-color: var(--idi-shade3-100);
  color: var(--txt-primary);
}
.deploy-panel__details-count {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 0.6875rem;
  color: var(--txt-secondary-2);
  background: var(--idi-shade1-100);
  padding: 2px 8px;
  border-radius: var(--rad-max);
}
.deploy-panel__details-list {
  list-style: none;
  margin: 8px 0 0;
  padding: 8px 14px;
  background: var(--idi-shade1-100);
  border: 1px solid var(--idi-shade3-25);
  border-radius: var(--rad-m);
  display: grid;
  gap: 6px;
}
.deploy-panel__details-item {
  display: grid;
  grid-template-columns: 160px 1fr auto;
  gap: 12px;
  align-items: baseline;
  font-size: 0.6875rem;
  line-height: 1.45;
}
.deploy-panel__details-item[data-failed-step='true'] {
  color: var(--danger-light);
  font-weight: 600;
}
.deploy-panel__details-step {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  color: var(--idi-green);
  letter-spacing: 0.02em;
}
.deploy-panel__details-msg {
  color: var(--txt-secondary-1);
}
.deploy-panel__details-time {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  color: var(--txt-secondary-2);
  font-variant-numeric: tabular-nums;
}

.deploy-panel__footnote {
  margin-top: 24px;
  font-size: 0.75rem;
  color: var(--txt-secondary-2);
}
</style>
